import { defineStore } from 'pinia'
import api from '@/api'
import Pusher from 'pusher-js'
import Echo from 'laravel-echo'
import { useAuthStore } from './auth'

export const useNotificationStore = defineStore('notification', {
  state: () => ({
    notifications: [],
    downloads: [],
    displayedNotification: null,
    notificationTimer: null,
    notificationAutoCloseTimer: null,
    notificationSound: new Audio('/notification.mp3')
  }),
  actions: {
    initLaravelEcho() {
      window.Pusher = Pusher

      window.Echo = new Echo({
        broadcaster: 'reverb',
        key: import.meta.env.VITE_REVERB_APP_KEY,
        wsHost: import.meta.env.VITE_REVERB_HOST,
        wsPort: import.meta.env.VITE_REVERB_PORT,
        wssPort: import.meta.env.VITE_REVERB_PORT,
        forceTLS: (import.meta.env.VITE_REVERB_SCHEME ?? 'https') === 'https',
        enabledTransports: ['ws', 'wss'],
        authEndpoint: `${import.meta.env.VITE_API_HOST}/broadcasting/auth`,
        bearerToken: localStorage.accessToken
      })

      const ecosystemAppId = import.meta.env.VITE_ECOSYSTEM_APP_ID
      const authStore = useAuthStore()
      window.Echo.private(`EcosystemApp.${ecosystemAppId}.User.${authStore.authUser.id}`)
        .listen('.NotificationAdd', (e) => {
          this.addNotification(e)
        })
        .listen('.NotificationRead', (e) => {
          this.markNotificationAsReadRemote(e.id, e.read_at)
        })
        .listen('.NotificationRemove', (e) => {
          this.deleteNotificationRemote(e.id)
        })
        .listen('.AlertAdd', (e) => {
          this.addAlert(e.data)
        })
        .listen('.DownloadAdd', (e) => {
          this.addDownload(e.data)
        })
        .listen('.DownloadRemove', (e) => {
          this.removeDownload(e.data.id)
        })

      window.Echo.connector.pusher.connection.bind('connected', () => {
        this.getNotifications()
        this.getDownloads()
      })
    },
    async getNotifications() {
      const response = await api.get(`v3/notifications`, {
        params: {
          ecosystem_app_id: import.meta.env.VITE_ECOSYSTEM_APP_ID
        }
      })
      this.notifications = response.data
    },
    addNotification(data) {
      const authStore = useAuthStore()
      this.notifications.unshift(data)

      clearTimeout(this.notificationTimer)
      this.dismissNotification()
      if (!authStore.authUser.do_not_disturb) {
        if (!authStore.authUser.mute_notifications) {
          this.notificationSound.play().catch(() => console.warn('Notification sound muted until user interaction.'))
        }
        this.notificationTimer = setTimeout(() => {
          this.displayedNotification = data

          this.startAutoHideTimer()
        }, 300)
      }
    },
    dismissNotification() {
      this.stopAutoHideTimer()
      this.displayedNotification = null
    },
    async deleteNotification(id) {
      this.deleteNotificationRemote(id)
      await api.delete(`v3/notifications/${id}`)
    },
    deleteNotificationRemote(id) {
      const notification = this.notifications.find((n) => n.id === id)
      if (!notification) {
        return
      }

      this.notifications.splice(this.notifications.indexOf(notification), 1)
    },
    async markNotificationAsRead(id) {
      let notification = this.notifications.find((n) => n.id === id)
      if (!notification) {
        return
      }

      const response = await api.post(`v3/notifications/${id}/read`)
      notification.read_at = response.data.read_at
    },
    markNotificationAsReadRemote(id, timestamp) {
      let notification = this.notifications.find((n) => n.id === id)
      if (!notification) {
        return
      }

      notification.read_at = timestamp
    },
    startAutoHideTimer() {
      if (!this.displayedNotification) {
        return
      }

      this.notificationAutoCloseTimer = setTimeout(() => {
        this.dismissNotification()
      }, 10000)
    },
    stopAutoHideTimer() {
      clearTimeout(this.notificationAutoCloseTimer)
    },
    async getDownloads() {
      const response = await api.get('v3/downloads', {
        params: {
          ecosystem_app_id: import.meta.env.VITE_ECOSYSTEM_APP_ID
        }
      })
      this.downloads = response.data
    },
    addDownload(data) {
      this.removeDownload(data.id)
      this.downloads.unshift(data)
    },
    removeDownload(id) {
      let download = this.downloads.find((d) => d.id === id)
      if (download) {
        this.downloads.splice(this.downloads.indexOf(download), 1)
      }
    },
    async deleteDownload(id) {
      this.removeDownload(id)
      await api.delete(`v3/downloads/${id}`)
    }
  }
})
